// File: src/i18n/index.js
/**
 * Vue i18n configuration for Lantern Frontend
 *
 * Provides internationalization support with dynamic locale loading,
 * security hardening, and localStorage persistence.
 *
 * ENVIRONMENT VARIABLES:
 * - VITE_DEBUG_I18N=true - Enable verbose i18n logging in development
 *
 * ENGLISH VARIANT RATIONALE:
 *
 * This implementation maintains separate English translations for different regions
 * rather than using a single "en" locale. This decision is based on:
 *
 * BUSINESS JUSTIFICATION:
 * - Market differentiation: Shows local market consideration and cultural awareness
 * - Customer trust: Users see their regional spelling/terminology (colour vs color)
 * - Competitive advantage: Most competitors use generic English, this personalizes UX
 * - Sales enablement: Helps sales teams demonstrate local market commitment
 * - Minimal cost: Fallback system means only differences need translation
 *
 * TECHNICAL JUSTIFICATION:
 * - Linguistic accuracy: Respects real-world English variant clustering around British base
 * - Reduced duplication: GB/IE/AU share 95% of spellings, minimizing override files
 * - Maintenance efficiency: Changes to base English propagate to all variants
 * - Bundle optimization: Only differences are loaded, keeping bundles small
 * - Scalability: Easy to add new variants (en-nz, en-za) with minimal overhead
 *
 * IMPLEMENTATION STRATEGY:
 * - Base (en): British English - serves GB, IE, AU with minimal overrides
 * - US variant (en-us): American spellings (color, realize, program)
 * - Regional variants: Only override necessary differences and local terms
 * - Fallback safety: Missing keys automatically use base English
 *
 * EXAMPLE IMPACT:
 * - British user sees: "colour", "realise", "programme" → feels "built for me"
 * - American user sees: "color", "realize", "program" → familiar experience
 * - Australian user sees: British spelling + "lift" instead of "elevator" → local touch
 * - Bundle size: ~95% shared content, <5% regional overrides
 */
import { createI18n } from 'vue-i18n'

/**
 * Default locale for the application
 * @constant {string}
 */
const DEFAULT_LOCALE = 'en'

/**
 * Whitelist of supported locales to prevent path traversal attacks
 *
 * SECURITY: This array acts as a safeguard against malicious locale values
 * that could potentially access files outside the locales directory.
 *
 * LOCALIZATION STRATEGY:
 * - Base English (en): British English - eliminates duplication across GB/IE/AU
 * - US English (en-us): Override for American spellings (color, realize, etc.)
 * - Irish English (en-ie): Minimal overrides (few Irish-specific terms)
 * - Australian English (en-au): Minimal overrides (Australian slang only)
 * - Canadian English (en-ca): Mixed overrides (mix of US/British conventions)
 * - Other languages: Base language only (fr, de) - regional variants canonicalized
 * - Technical benefit: Eliminates 95% duplication between GB/IE/AU override files
 *
 * @constant {string[]}
 */
const SUPPORTED_LOCALES = Object.freeze(['en', 'en-us', 'en-ie', 'en-au', 'en-ca', 'fr']) // keep in sync with files present

/**
 * Idiomatic fallback chain for Vue i18n
 *
 * STRATEGY: Use Vue i18n's built-in fallback resolution instead of manual merging.
 * This keeps messages atomic (per-file) and simplifies HMR behavior.
 *
 * FALLBACK LOGIC:
 * - English variants (en-us, en-ie, etc.) → fall back to British English base ('en')
 * - Other languages (fr) → fall back to English for missing business terms
 * - Keeps source-of-truth separated for easier debugging and authoring
 *
 * @constant {Object}
 */
const FALLBACK_MAP = {
    'en-us': ['en-us', 'en'],
    'en-ie': ['en-ie', 'en'],
    'en-au': ['en-au', 'en'],
    'en-ca': ['en-ca', 'en'],
    fr: ['fr', 'en'], // Business choice: English fallback for missing French
    default: 'en'
}

/**
 * Safe localStorage operations with error handling
 *
 * ROBUSTNESS: Prevents crashes in SSR environments, private browsing mode,
 * or corporate environments where localStorage may be disabled or throw errors.
 *
 * @constant {Object}
 */
const safeStorage = {
    get: (key) => {
        try {
            return localStorage.getItem(key)
        } catch {
            return null
        }
    },
    set: (key, value) => {
        try {
            localStorage.setItem(key, value)
        } catch {
            // Silent fail - don't break the app if storage is disabled
        }
    }
}

/**
 * Smart locale canonicalization with English variant preservation
 *
 * STRATEGY:
 * - English variants (en-us, en-ie, en-au, en-ca): Preserved if supported, fallback to 'en' (GB)
 * - Other languages: Regional variants stripped (fr-ca → fr, de-at → de)
 * - Handles case insensitivity and malformed input gracefully
 *
 * @param {string} locale - Raw locale input (e.g., 'EN-US', 'fr-CA', 'en-nz')
 * @returns {string} Canonicalized locale guaranteed to be in SUPPORTED_LOCALES
 *
 * @example
 * canonicalize('EN-US')    // → 'en-us' (preserved English variant)
 * canonicalize('en-nz')    // → 'en'    (unsupported variant → British English)
 * canonicalize('fr-CA')    // → 'fr'    (French regional stripped)
 * canonicalize('invalid')  // → 'en'    (fallback to British English)
 */
const canonicalize = (locale) => {
    if (!locale || typeof locale !== 'string') return DEFAULT_LOCALE

    const normalized = locale.toLowerCase().trim()

    // If it's already in our supported list, use it (handles all en-* variants)
    if (SUPPORTED_LOCALES.includes(normalized)) {
        return normalized
    }

    // For non-English languages, strip regional variant (fr-ca → fr, de-at → de)
    const base = normalized.split('-')[0]
    return SUPPORTED_LOCALES.includes(base) ? base : DEFAULT_LOCALE
}

/**
 * Vite-optimized locale module map for efficient dynamic imports
 *
 * PERFORMANCE: Uses import.meta.glob for build-time optimization and tree shaking.
 * Vite will pre-analyze these imports and optimize the module graph.
 *
 * @constant {Record<string, () => Promise<any>>}
 */
const localeModules = import.meta.glob('./locales/*.json')

/**
 * Simple locale file loader with Vite optimization
 *
 * Loads individual locale JSON files using Vite's import.meta.glob for
 * build-time optimization. No manual caching in development to ensure
 * clean HMR behavior.
 *
 * PERFORMANCE: Uses import.meta.glob for Vite build optimization and tree shaking
 * SECURITY: Validates locale against SUPPORTED_LOCALES whitelist
 * HMR: No manual caching in dev - let Vite handle module replacement cleanly
 *
 * @param {string} locale - The locale to load (e.g., 'en', 'fr', 'en-us')
 * @returns {Promise<Object>} The locale messages object
 *
 * @example
 * const messages = await loadLocaleFile('en-us')
 * // Returns: Only the en-us.json content (fallback handled by Vue i18n)
 */
const loadLocaleFile = async (locale) => {
    const safe = SUPPORTED_LOCALES.includes(locale) ? locale : DEFAULT_LOCALE
    const modulePath = `./locales/${safe}.json`

    try {
        if (localeModules[modulePath]) {
            const mod = await localeModules[modulePath]()
            return mod?.default || {}
        } else {
            if (import.meta.env.DEV && import.meta.env.VITE_DEBUG_I18N === 'true') {
                console.warn(`[i18n] Module not found for locale "${safe}"`)
            }
            return {}
        }
    } catch (e) {
        if (import.meta.env.DEV && import.meta.env.VITE_DEBUG_I18N === 'true') {
            console.warn(`[i18n] Failed to load locale "${safe}"`, e)
        }
        return {}
    }
}

/**
 * Race-safe locale loader with request deduplication
 *
 * CONCURRENCY: Coalesces multiple concurrent requests for the same locale
 * to prevent race conditions and duplicate network/processing.
 *
 * @constant {Map<string, Promise<Object>>} In-flight request cache
 */
const inflight = new Map()

/**
 * Load locale with deduplication for concurrent requests
 *
 * RACE-SAFE: If multiple components call setLocale() quickly or user double-clicks,
 * this ensures only one actual load per locale and all callers get the same result.
 *
 * @param {string} locale - The locale to load
 * @returns {Promise<Object>} The locale messages object (deduplicated)
 */
const loadOnce = async (locale) => {
    if (!inflight.has(locale)) {
        inflight.set(
            locale,
            loadLocaleFile(locale).finally(() => inflight.delete(locale))
        )
    }
    return inflight.get(locale)
}

/**
 * Retrieves the initial locale from localStorage or falls back to browser detection
 *
 * SECURITY: Clamps the stored locale to the SUPPORTED_LOCALES whitelist
 * to prevent malicious values from being persisted or used.
 *
 * ROBUSTNESS: Uses safe localStorage access to handle SSR and privacy modes.
 * Guards navigator access for SSR/testing environments.
 *
 * LOCALIZATION: Applies smart canonicalization to handle English variants
 * and regional codes appropriately.
 *
 * UX: On first visit, detects user's preferred language from browser Accept-Language
 * headers, providing immediate localization without user interaction.
 *
 * @returns {string} The initial locale to use (guaranteed to be in whitelist)
 *
 * @example
 * // First visit with browser set to 'en-GB'
 * getInitialLocale() // → 'en' (British English base)
 *
 * // First visit with browser set to 'en-US'
 * getInitialLocale() // → 'en-us' (American English variant)
 *
 * // Returning user with stored preference
 * getInitialLocale() // → stored preference (if valid)
 */
const getInitialLocale = () => {
    const stored = safeStorage.get('locale')
    if (stored) return canonicalize(stored)

    // SSR/testing guard: only detect browser locale if navigator exists
    const hasNavigator = typeof navigator !== 'undefined'
    const browserLocale = hasNavigator
        ? navigator.languages?.[0] || navigator.language
        : DEFAULT_LOCALE
    return canonicalize(browserLocale || DEFAULT_LOCALE)
}

/**
 * Vue i18n instance configuration
 *
 * Creates the main i18n instance with security-hardened settings,
 * modern Vue 3 Composition API support, and idiomatic fallback chains.
 *
 * CONFIGURATION RATIONALE:
 * - legacy: false - Use Composition API instead of legacy $t syntax
 * - locale: Dynamic from localStorage with fallback
 * - fallbackLocale: FALLBACK_MAP defines chains (en-us → en → default)
 * - messages: Start empty - load locales async to avoid bundle bloat
 * - globalInjection: true - Enables $t() in templates globally
 * - escapeParameter: true - XSS protection by escaping interpolated values
 * - warnHtmlMessage: false - Reduces console noise for HTML in translations
 * - missing: Custom handler for missing translation keys (dev-only warnings)
 * - missingWarn: Enable missing key warnings in development
 * - fallbackWarn: Disable fallback warnings to reduce console noise
 *
 * @see https://vue-i18n.intlify.dev/api/general.html#createi18n
 */
const i18n = createI18n({
    legacy: false,
    locale: DEFAULT_LOCALE,
    fallbackLocale: FALLBACK_MAP, // Idiomatic fallback chains instead of manual merging
    messages: {}, // Start empty - load default locale async
    globalInjection: true,
    escapeParameter: true, // escape interpolated values (security)
    warnHtmlMessage: false, // silence HTML-in-message warnings if we don't inject raw HTML
    missing: (locale, key) => {
        if (import.meta.env.DEV && import.meta.env.VITE_DEBUG_I18N === 'true') {
            console.warn(`[i18n] Missing key "${key}" for ${locale}`)
        }
    },
    missingWarn: import.meta.env.DEV,
    fallbackWarn: false
})

/**
 * Defensive guard: Ensure base English is loaded before any locale operations
 *
 * ROBUSTNESS: Prevents issues if setLocale() is called before initializeI18n().
 * Always ensures fallback chains have their base locale available.
 *
 * @returns {Promise<void>} Promise that resolves when base English is available
 */
const ensureBaseLoaded = async () => {
    if (!i18n.global.availableLocales.includes('en')) {
        const enMessages = await loadOnce('en')
        i18n.global.setLocaleMessage('en', enMessages)
    }
}

/**
 * Dynamically switches the application locale
 *
 * Loads translation messages if not already loaded and updates the active locale.
 * Persists the selection to localStorage for future sessions.
 *
 * IDIOMATIC APPROACH: Loads only the target locale file. Vue i18n's fallback
 * chain (defined in FALLBACK_MAP) handles missing keys automatically.
 *
 * SECURITY: Clamps input locale to SUPPORTED_LOCALES whitelist to prevent
 * injection of malicious locale values.
 *
 * ROBUSTNESS: Uses safe localStorage access to handle SSR and privacy modes.
 *
 * @param {string} locale - Target locale code (e.g., 'en-us', 'fr-ca', 'en-au')
 * @returns {Promise<void>} Promise that resolves when locale is loaded and active
 *
 * @example
 * import { setLocale } from '@/i18n'
 * await setLocale('en-US')  // Normalized to 'en-us' and applied
 * await setLocale('fr-CA')  // Normalized to 'fr' and applied
 * await setLocale('en-NZ')  // Normalized to 'en' (British English fallback)
 */
export const setLocale = async (locale) => {
    // DEFENSIVE: Ensure base English is always available for fallback chains
    await ensureBaseLoaded()

    // SECURITY & LOCALIZATION: Canonicalize and clamp to whitelist
    const safeLocale = canonicalize(locale)

    // BELT-AND-BRACES: Early exit with warning if somehow unsupported locale reaches here
    if (!SUPPORTED_LOCALES.includes(safeLocale)) {
        if (import.meta.env.DEV && import.meta.env.VITE_DEBUG_I18N === 'true') {
            console.warn(
                `[i18n] Attempted to set unsupported locale "${locale}" (canonicalized to "${safeLocale}"). Skipping locale change.`
            )
        }
        return // Skip changing locale, keep current
    }

    const alreadyLoaded =
        i18n.global.availableLocales.includes(safeLocale) &&
        Object.keys(i18n.global.getLocaleMessage(safeLocale)).length > 0

    if (!alreadyLoaded) {
        const messages = await loadOnce(safeLocale)
        i18n.global.setLocaleMessage(safeLocale, messages)
    }

    i18n.global.locale.value = safeLocale
    safeStorage.set('locale', safeLocale)
}

/**
 * Get the list of supported locales
 *
 * Utility function to expose supported locales for UI components
 * (e.g., language selection dropdowns)
 *
 * @returns {string[]} Array of supported locale codes
 *
 * @example
 * import { getSupportedLocales } from '@/i18n'
 * const locales = getSupportedLocales() // ['en', 'en-us', 'en-ie', 'en-au', 'en-ca', 'fr']
 */
export const getSupportedLocales = () => [...SUPPORTED_LOCALES]

/**
 * Get the current active locale
 *
 * Utility function for UI components and stores to check current locale
 * without importing the entire i18n instance.
 *
 * @returns {string} Current locale code (e.g., 'en-us', 'fr')
 *
 * @example
 * import { currentLocale } from '@/i18n'
 * const locale = currentLocale() // 'en-us'
 */
export const currentLocale = () => i18n.global.locale.value

/**
 * Check if a locale is supported (with canonicalization)
 *
 * Utility function to validate locale codes in UI components.
 * Applies the same canonicalization rules as the core system.
 *
 * @param {string} locale - Locale code to check (e.g., 'EN-US', 'fr-ca')
 * @returns {boolean} True if locale is supported (after canonicalization)
 *
 * @example
 * import { isSupportedLocale } from '@/i18n'
 * isSupportedLocale('EN-US') // true (canonicalized to 'en-us')
 * isSupportedLocale('zh-cn') // false (not in SUPPORTED_LOCALES)
 */
export const isSupportedLocale = (locale) => SUPPORTED_LOCALES.includes(canonicalize(locale))

/**
 * Initialize i18n with idiomatic fallback strategy
 *
 * STRATEGY: Always register base English ('en') first to ensure Vue i18n's
 * fallback chains work correctly. Then load and switch to the user's preferred locale.
 *
 * IDIOMATIC APPROACH: Leverages Vue i18n's built-in fallback resolution instead
 * of manual merging. Keeps source files atomic for better debugging and HMR.
 *
 * @returns {Promise<void>} Promise that resolves when i18n is fully initialized
 *
 * @example
 * await initializeI18n()
 * // Result: Base English registered + user's locale active with fallback chain
 */
export const initializeI18n = async () => {
    // Resolve and persist the locale BEFORE any async work so that the pre-Vue
    // loading overlay (which reads localStorage synchronously) always has the
    // correct locale available — even if a subsequent await never completes.
    const initialLocale = getInitialLocale()

    // Only persist the resolved locale when it genuinely reflects this user's
    // preference — i.e. there was already a stored locale (refresh/normalise it),
    // or the browser locale is natively supported by the app.
    //
    // When neither is true, getInitialLocale() silently falls back to DEFAULT_LOCALE
    // ('en'). Writing that fallback would clobber navigator.language in localStorage,
    // preventing the loading-messages overlay from showing the user's actual language.
    // The overlay ships translations for far more base languages than the app supports,
    // so leaving localStorage empty lets it use navigator.language correctly.
    const hasStoredLocale = !!safeStorage.get('locale')
    if (hasStoredLocale || initialLocale !== DEFAULT_LOCALE) {
        safeStorage.set('locale', initialLocale)
    }

    // Always ensure base English is present in the runtime for fallback chains
    const enMessages = await loadOnce('en')
    i18n.global.setLocaleMessage('en', enMessages)

    if (initialLocale === 'en') {
        i18n.global.locale.value = 'en'
        return
    }

    const messages = await loadOnce(initialLocale)
    i18n.global.setLocaleMessage(initialLocale, messages)
    i18n.global.locale.value = initialLocale
}

/**
 * Hot Module Replacement for locale files (Development only)
 *
 * DEVELOPER EXPERIENCE: Live-reloads active locale files when JSON is edited.
 * Automatically reloads base English + current locale for instant feedback.
 *
 * @see https://vitejs.dev/guide/api-hmr.html#hot-accept-cb
 */
if (import.meta.hot) {
    import.meta.hot.accept(Object.keys(localeModules), async () => {
        const current = i18n.global.locale.value || 'en'
        const en = await loadOnce('en')
        i18n.global.setLocaleMessage('en', en)
        if (current !== 'en') {
            const msgs = await loadOnce(current)
            i18n.global.setLocaleMessage(current, msgs)
        }
    })
}

export default i18n
